import {
    type CreateExtensionArgument,
    type CreateExtArgs,
    type Extension,
    type ExtensionAPI,
    pointerUtils
} from '@wix/document-manager-core'
import type {BuilderStyle} from '@wix/document-services-types'
import {CSS_SHORTHAND_MAPPINGS} from '../builderComponents/shorthandMappings'
import {isEmpty, pick} from 'lodash'
import type {VariablesExtensionAPI} from '../variables/variables'
import {ReportableError} from '@wix/document-manager-utils'
import {DATA_TYPES} from '../../constants/constants'

export interface CssStyleExtensionAPI extends ExtensionAPI {
    cssStyle: {
        getPublicCSSStyle(styleItem: BuilderStyle): BuilderStyle
        getUpdatedCSSStyleItemForRemove(existingStyle: BuilderStyle, paths: string[]): BuilderStyle | undefined
        getUpdatedCSSStyleItemForUpdate(
            existingStyle: BuilderStyle,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties?: boolean
        ): BuilderStyle
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): CssStyleExtensionAPI => {
        const {variables} = extensionAPI as VariablesExtensionAPI

        const removeExpandedProperties = (
            existingProperties: BuilderStyle['cssCustomProperties'],
            propertiesToRemoveExpansions: string[]
        ) => {
            const propertiesToRemove = new Set<string>()
            const toProcess = propertiesToRemoveExpansions.slice()
            const processed = new Set<string>()

            while (toProcess.length > 0) {
                const currentProperty = toProcess.pop()!

                // Skip if already processed
                if (processed.has(currentProperty)) {
                    continue
                }

                processed.add(currentProperty)

                // Look up expansions for this property
                const expansions = CSS_SHORTHAND_MAPPINGS[currentProperty]
                if (expansions) {
                    // Add all expansion property names to both removal set and processing array
                    expansions.forEach(({propertyName}) => {
                        if (!processed.has(propertyName)) {
                            propertiesToRemove.add(propertyName)
                            toProcess.push(propertyName)
                        }
                    })
                }
            }

            // Remove all collected cssProperties from styleItem
            propertiesToRemove.forEach(propertyName => {
                delete existingProperties![propertyName]
            })
        }

        const updateCssVariableConnections = (styleItem: BuilderStyle): BuilderStyle => {
            const globalVariablesSet = new Set(variables.getGlobalVariableIds())
            const nonExistingGlobalVariables = new Map()

            const propsKeys = ['cssProperties', 'cssCustomProperties'] as const
            propsKeys.forEach(key => {
                if (styleItem[key]?.variableConnections) {
                    delete styleItem[key]!.variableConnections
                }

                Object.entries(styleItem[key] ?? {}).forEach(([property, value]) => {
                    if (typeof value === 'string' && value.startsWith('var(--')) {
                        const variableId = value.slice(6, -1)

                        if (!globalVariablesSet.has(variableId)) {
                            nonExistingGlobalVariables.set(`${key}/${property}`, variableId)
                        }

                        if (nonExistingGlobalVariables.size > 0) {
                            return
                        }

                        variables.connect(
                            styleItem[key],
                            property,
                            pointerUtils.getPointer(variableId, DATA_TYPES.variables)
                        )
                    }
                })
            })

            if (nonExistingGlobalVariables.size > 0) {
                const nonExistingGlobalVariablesObj = Object.fromEntries(nonExistingGlobalVariables.entries())
                throw new ReportableError({
                    message: `updateCSSStyle sent with non-existing global variables: ${JSON.stringify(
                        nonExistingGlobalVariablesObj
                    )}`,
                    errorType: 'updateCSSStyleInvalidVariable',
                    extras: {
                        nonExistingGlobalVariables: nonExistingGlobalVariablesObj
                    }
                })
            }

            return styleItem
        }

        //If there is no existing style, you must still pass an object with {type: TheStyleType} to this function
        const getUpdatedCSSStyleItemForUpdate = (
            existingStyle: BuilderStyle,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties: boolean = true
        ) => {
            const {cssProperties = {}, cssCustomProperties = {}, type} = existingStyle

            // Handle cssProperties update
            if (partialStyle.cssProperties) {
                if (shouldRemoveRelatedProperties && Object.keys(cssProperties).length > 0) {
                    removeExpandedProperties(cssProperties, Object.keys(partialStyle.cssProperties))
                }
                Object.assign(cssProperties, partialStyle.cssProperties)
            }

            // Handle cssCustomProperties update
            if (partialStyle.cssCustomProperties) {
                Object.assign(cssCustomProperties, partialStyle.cssCustomProperties)
            }

            // Handle spx update
            const newStyleItem: any = {
                ...existingStyle,
                type
            }
            //only assign if we had an update:
            if (partialStyle.cssProperties) {
                newStyleItem.cssProperties = cssProperties
            }

            if (partialStyle.cssCustomProperties) {
                newStyleItem.cssCustomProperties = cssCustomProperties
            }

            if (partialStyle.spx !== undefined) {
                newStyleItem.spx = partialStyle.spx
            }

            return updateCssVariableConnections(newStyleItem)
        }

        const getUpdatedCSSStyleItemForRemove = (
            styleItem: BuilderStyle,
            paths: string[]
        ): BuilderStyle | undefined => {
            const pathHandlers: Record<string, (key?: string) => boolean> = {
                cssProperties: (key?: string) => {
                    if (key) {
                        if (styleItem.cssProperties?.[key]) {
                            delete styleItem.cssProperties[key]
                            return true
                        }
                        return false
                    }
                    // @ts-ignore
                    styleItem.cssProperties = null
                    return true
                },
                cssCustomProperties: (key?: string) => {
                    if (key) {
                        if (styleItem.cssCustomProperties?.[key]) {
                            delete styleItem.cssCustomProperties[key]
                            return true
                        }
                        return false
                    }
                    // @ts-ignore
                    styleItem.cssCustomProperties = null
                    return true
                },
                spx: () => {
                    if (styleItem.spx) {
                        // @ts-ignore
                        styleItem.spx = null
                        return true
                    }
                    return false
                }
            }

            const removePath = (path: string): boolean => {
                const [property, key] = path.split('.', 2)
                return pathHandlers[property]?.(key) || false
            }

            const hasChanges = paths.map(removePath).some(Boolean)

            if (hasChanges) {
                // Clean up empty objects - dataModel will remove null properties on update
                if (isEmpty(styleItem.cssCustomProperties)) {
                    // @ts-ignore
                    styleItem.cssCustomProperties = null
                }
                if (isEmpty(styleItem.cssProperties)) {
                    // @ts-ignore
                    styleItem.cssProperties = null
                }

                return updateCssVariableConnections(styleItem)
            }
        }

        const getPublicCSSStyle = (styleItem: BuilderStyle): BuilderStyle => {
            delete styleItem?.cssProperties?.variableConnections
            delete styleItem?.cssCustomProperties?.variableConnections
            return pick(styleItem, ['cssProperties', 'cssCustomProperties', 'spx', 'name', 'type'])
        }
        return {
            cssStyle: {
                getPublicCSSStyle,
                getUpdatedCSSStyleItemForRemove,
                getUpdatedCSSStyleItemForUpdate
            }
        }
    }

    return {
        name: 'cssStyle',
        dependencies: new Set(['variables']),
        createExtensionAPI
    }
}

export {createExtension}
